import React from 'react';

const renderContent = (content) => {
  if (!content) return null;

  // String content: split into paragraphs
  if (typeof content === 'string') {
    const paragraphs = content.split(/\n\n+/).filter(p => p.trim());
    if (paragraphs.length <= 1) {
      return <p className="text-gray-700 mb-4 leading-relaxed">{content}</p>;
    }
    return (
      <div className="text-gray-700 mb-4 space-y-3">
        {paragraphs.map((p, i) => (
          <p key={i} className="leading-relaxed">{p.trim()}</p>
        ))}
      </div>
    );
  }

  // Array content (e.g., internal sections with list data)
  if (Array.isArray(content)) {
    if (content.length === 0) return <p className="text-gray-500 italic">No data available.</p>;
    return (
      <div className="space-y-2 mb-4">
        {content.map((item, i) => (
          <div key={i} className="bg-gray-50 rounded-lg p-3 border border-gray-100">
            {typeof item === 'string' ? (
              <p className="text-gray-700">{item}</p>
            ) : (
              <div className="space-y-1">
                {Object.entries(item).map(([key, val]) => (
                  <div key={key} className="flex gap-2">
                    <span className="text-xs font-medium text-gray-500 uppercase min-w-[100px]">{key.replace(/_/g, ' ')}:</span>
                    <span className="text-sm text-gray-700">
                      {typeof val === 'object' ? JSON.stringify(val, null, 2) : String(val ?? 'N/A')}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    );
  }

  // Object content (e.g., internal sections with dict data)
  if (typeof content === 'object') {
    return (
      <div className="space-y-3 mb-4">
        {Object.entries(content).map(([key, val]) => (
          <div key={key}>
            <h5 className="text-sm font-semibold text-gray-600 uppercase mb-1">{key.replace(/_/g, ' ')}</h5>
            {typeof val === 'string' ? (
              <p className="text-gray-700 leading-relaxed">{val}</p>
            ) : Array.isArray(val) ? (
              <ul className="list-disc list-inside text-gray-700 space-y-1 ml-2">
                {val.map((item, i) => (
                  <li key={i} className="text-sm">
                    {typeof item === 'object' ? (
                      <span>{Object.values(item).filter(v => typeof v === 'string').join(' - ')}</span>
                    ) : String(item)}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-gray-700">{String(val ?? 'N/A')}</p>
            )}
          </div>
        ))}
      </div>
    );
  }

  return <p className="text-gray-700 mb-4">{String(content)}</p>;
};

const ProposalSectionView = ({ section, hideTitle = false }) => {
  if (!section) return null;

  return (
    <div className={hideTitle ? '' : 'mb-8'}>
      {!hideTitle && (
        <h3 className="text-xl font-bold text-gray-900 mb-3">
          {section.section_number}. {section.title}
        </h3>
      )}

      {section.content && renderContent(section.content)}

      {/* Key Metrics */}
      {section.key_metrics && section.key_metrics.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3 mb-4">
          {section.key_metrics.map((metric, idx) => (
            <div key={idx} className="bg-primary-50 rounded-lg p-3 text-center">
              <p className="text-xs text-primary-600 font-medium uppercase">{metric.label}</p>
              <p className="text-lg font-bold text-primary-900 mt-0.5">{metric.value}</p>
            </div>
          ))}
        </div>
      )}

      {/* Subsections */}
      {section.subsections && section.subsections.map((sub, idx) => (
        <div key={idx} className="mb-4">
          {sub.subtitle && (
            <h4 className="text-lg font-semibold text-gray-800 mb-2">{sub.subtitle}</h4>
          )}
          {sub.content && (
            <p className="text-gray-700 mb-2 leading-relaxed">{sub.content}</p>
          )}
          {sub.bullet_points && (
            <ul className="list-disc list-inside text-gray-700 space-y-1 ml-2">
              {sub.bullet_points.map((point, i) => (
                <li key={i}>{point}</li>
              ))}
            </ul>
          )}
          {sub.highlight_metrics && sub.highlight_metrics.length > 0 && (
            <div className="flex flex-wrap gap-3 mt-2">
              {sub.highlight_metrics.map((m, i) => (
                <div key={i} className="bg-gray-50 border border-gray-200 rounded px-3 py-2">
                  <span className="text-xs text-gray-500">{m.label}: </span>
                  <span className="text-sm font-semibold text-gray-900">{m.value}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

export default ProposalSectionView;
